import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { CheckCircle, Target, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';


interface ServicesProps {
    onCalendlyRedirect: () => void;
}

export default function Services({ onCalendlyRedirect }: ServicesProps) {
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    
    useEffect(() => {
        // Martes 24 de Marzo 2026 a las 00:00:00 (inicio del bootcamp)
        const targetDate = new Date('2026-03-24T00:00:00').getTime();
        
        const updateTimer = () => {
            const now = new Date().getTime();
            const difference = targetDate - now;
            
            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((difference % (1000 * 60)) / 1000)
                });
            } else {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        
        return () => clearInterval(interval);
    }, []);
    
    return (
        <section id="servicios" className="py-24">
            <div className="max-w-6xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-green-400 mb-6">
                            Acelerá tu carrera profesional
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Bootcamp intensivo o mentorías 1:1. El primer paso es una llamada si no sabés cuál te conviene.
                        </p>
                        <div className="w-20 h-1 bg-green-500 mx-auto mt-6"></div>
                    </div>

                    {/* Dos cards: Bootcamp (protagonista) + Mentorías */}
                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
                        {/* Card Bootcamp - protagonista */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <Card id="bootcamp" className="bg-gray-900/70 border-2 border-purple-500/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
                            <CardHeader className="text-center pb-4">
                                <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center">
                                    <Sparkles className="w-8 h-8 text-purple-400" />
                                </div>
                                <CardTitle className="text-xl font-bold text-gray-100">
                                    Bootcamp: De Cero a Data Engineer
                                </CardTitle>
                                <div className="mt-3 flex items-center justify-center">
                                    <motion.div 
                                        className="bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-3 flex items-center gap-2"
                                        animate={{
                                            opacity: [0.7, 1, 0.7],
                                            scale: [0.98, 1, 0.98]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                                        <span className="text-lg font-bold text-red-400">Próxima cohorte Marzo 2026</span>
                                    </motion.div>
                                </div>
                                <div className="text-3xl font-bold mt-3 flex flex-col items-center gap-2">
                                    <span className="text-green-400">$495 USD preventa</span>
                                    <span className="text-xs text-amber-400 font-semibold mt-1">Solo preventa por tiempo limitado</span>
                                </div>
                                <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-purple-400" />
                                        <span className="text-xs font-semibold text-gray-300">Inicio del bootcamp:</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-2 text-center">
                                        <div>
                                            <div className="text-lg font-bold text-gray-100">{timeLeft.days}</div>
                                            <div className="text-xs text-gray-400">Días</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-100">{timeLeft.hours}</div>
                                            <div className="text-xs text-gray-400">Horas</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-100">{timeLeft.minutes}</div>
                                            <div className="text-xs text-gray-400">Min</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-100">{timeLeft.seconds}</div>
                                            <div className="text-xs text-gray-400">Seg</div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-400 text-center mt-2">
                                        Inicio: Martes 24 de Marzo
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4 pt-4 flex-grow flex flex-col">
                                <p className="text-center text-gray-300 italic text-sm">
                                    Aprendé a resolver los problemas que resuelven los ingenieros de datos de hoy
                                </p>
                                <div className="space-y-2 text-gray-300 flex-grow">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm"><strong>Databricks, y SQL en profundidad</strong> para pasar entrevistas técnicas</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm"><strong>Fundamentos de Ingeniería de Datos</strong> desde cero</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm"><strong>Proyecto final real</strong> para tu portfolio profesional</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm"><strong>Herramientas más demandadas</strong> por el mercado hoy</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        </motion.div>

                        {/* Card Mentorías */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <Card 
                                className="bg-gray-900/70 border-2 border-green-500/20 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col"
                            >
                                <CardHeader className="text-center pb-4">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
                                        <Target className="w-8 h-8 text-green-400" />
                                    </div>
                                    <CardTitle className="text-xl font-bold text-gray-100 mb-2">
                                        Mentorías Personalizadas
                                    </CardTitle>
                                    <div className="space-y-2">
                                        <div className="text-3xl font-bold text-green-400">
                                            Desde $100 USD
                                        </div>
                                        <p className="text-sm text-gray-300 mt-3">
                                            Empezá con una Sesión Diagnóstico
                                        </p>
                                        <div className="mt-4 flex items-center justify-center">
                                            <motion.div 
                                                className="bg-red-500/20 border-2 border-red-500 rounded-xl px-5 py-3 flex flex-col items-center gap-1 shadow-lg"
                                                animate={{
                                                    opacity: [0.7, 1, 0.7],
                                                    scale: [0.98, 1, 0.98]
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "easeInOut"
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                                                    <span className="text-lg font-bold text-red-400">Cupos limitados</span>
                                                </div>
                                                <span className="text-xs text-red-400">Sesión Diagnóstico + Paquetes</span>
                                            </motion.div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4 pt-4 flex-grow flex flex-col">
                                    <div className="space-y-2 text-gray-300 flex-grow flex flex-col items-center">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Sesiones de 1 hora</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Plan de carrera personalizado</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Roadmap completo de 3 meses</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Acceso a bolsa de trabajo exclusiva</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Simulacros de entrevistas ilimitados</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">Revisión completa de CV y portfolio</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm">LinkedIn optimizado que trabaja 24/7</span>
                                        </div>
                                    </div>
                                    <Link to="/mentorias" className="mt-4">
                                        <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                            Ver planes y precios
                                            <ArrowRight className="ml-2 w-4 h-4" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>

                    {/* Un solo CTA verde debajo de las dos cards */}
                    <div className="flex justify-center mt-10">
                        <Button
                            size="lg"
                            className="bg-green-600 hover:bg-green-700 text-white py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base font-semibold"
                            onClick={onCalendlyRedirect}
                        >
                            <Sparkles className="mr-2 w-5 h-5" />
                            Contactame y empecemos
                        </Button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
